import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import UsageDisplay from '../components/UsageDisplay';
import Header from '../components/Header';
import api from '../services/api';

type Lead = {
  id: number;
  status: string;
  follow_up_date?: string | null;
  created_at: string;
};

const Dashboard: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadsError, setLeadsError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLeadsLoading(true);
        const res = await api.get('/leads');
        setLeads(res.data || []);
        setLeadsError(null);
      } catch (err: any) {
        setLeadsError(err.response?.data?.detail || 'Error loading leads');
      } finally {
        setLeadsLoading(false);
      }
    };
    load();
  }, []);

  const leadStats = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const l of leads) {
      const key = (l.status || 'New').toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    }

    const todayStr = new Date().toISOString().slice(0, 10);
    const followupsDue = leads.filter((l) => {
      if (!l.follow_up_date) return false;
      return String(l.follow_up_date).slice(0, 10) <= todayStr;
    }).length;

    const total = leads.length;
    const newCount = counts['new'] || 0;
    const contacted = counts['contacted'] || 0;
    const interested = counts['interested'] || 0;

    return { total, newCount, contacted, interested, followupsDue };
  }, [leads]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header activeTab="intelligence" />
      
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-12">
        <UsageDisplay />
        
        <div className="bg-white rounded-2xl border border-slate-200 p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="text-3xl font-black text-slate-900 mb-2">Dashboard</h2>
              <p className="text-slate-600">
                Quick snapshot of your pipeline and what to do next.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/search"
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition-all"
              >
                New search
              </Link>
              <Link
                to="/leads"
                className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-bold hover:bg-slate-200 transition-all"
              >
                Open pipeline
              </Link>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div className="text-xs font-black uppercase tracking-widest text-slate-500">Total leads</div>
              <div className="text-3xl font-black text-slate-900 mt-2">
                {leadsLoading ? '—' : leadStats.total}
              </div>
            </div>
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div className="text-xs font-black uppercase tracking-widest text-slate-500">New</div>
              <div className="text-3xl font-black text-indigo-600 mt-2">
                {leadsLoading ? '—' : leadStats.newCount}
              </div>
            </div>
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div className="text-xs font-black uppercase tracking-widest text-slate-500">Contacted</div>
              <div className="text-3xl font-black text-slate-900 mt-2">
                {leadsLoading ? '—' : leadStats.contacted}
              </div>
            </div>
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div className="text-xs font-black uppercase tracking-widest text-slate-500">Interested</div>
              <div className="text-3xl font-black text-slate-900 mt-2">
                {leadsLoading ? '—' : leadStats.interested}
              </div>
            </div>
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
              <div className="text-xs font-black uppercase tracking-widest text-amber-700">Follow-ups due</div>
              <div className="text-3xl font-black text-amber-700 mt-2">
                {leadsLoading ? '—' : leadStats.followupsDue}
              </div>
            </div>
          </div>

          {leadsError && (
            <div className="mt-6 p-4 bg-rose-50 text-rose-700 rounded-xl border border-rose-100">
              {leadsError}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="text-lg font-black text-slate-900 mb-1">Next best action</h3>
            <p className="text-slate-600 text-sm mb-4">
              {leadsLoading
                ? 'Loading your pipeline…'
                : leadStats.total === 0
                  ? 'Start by running your first search.'
                  : leadStats.followupsDue > 0
                    ? `You have ${leadStats.followupsDue} follow-up${leadStats.followupsDue === 1 ? '' : 's'} due.`
                    : `You have ${leadStats.newCount} new lead${leadStats.newCount === 1 ? '' : 's'} to contact.`}
            </p>
            <div className="flex gap-3">
              <Link
                to={leadStats.total === 0 ? '/search' : '/leads'}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition-all text-sm"
              >
                {leadStats.total === 0 ? 'Start search' : 'Go to pipeline'}
              </Link>
              <Link
                to="/search"
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-bold hover:bg-slate-200 transition-all text-sm"
              >
                Search
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="text-lg font-black text-slate-900 mb-1">Pipeline health</h3>
            <p className="text-slate-600 text-sm mb-4">
              Balanced pipelines usually have a mix of new and contacted leads.
            </p>
            <div className="text-sm text-slate-700 space-y-2">
              <div className="flex justify-between">
                <span className="font-semibold">New</span>
                <span>{leadsLoading ? '—' : leadStats.newCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Contacted</span>
                <span>{leadsLoading ? '—' : leadStats.contacted}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Interested</span>
                <span>{leadsLoading ? '—' : leadStats.interested}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="text-lg font-black text-slate-900 mb-1">Shortcuts</h3>
            <p className="text-slate-600 text-sm mb-4">Jump right back into your workflow.</p>
            <div className="flex flex-col gap-3">
              <Link
                to="/search"
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-bold hover:bg-slate-200 transition-all text-sm"
              >
                Business search
              </Link>
              <Link
                to="/leads"
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-bold hover:bg-slate-200 transition-all text-sm"
              >
                Pipeline
              </Link>
              <Link
                to="/profile"
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-bold hover:bg-slate-200 transition-all text-sm"
              >
                Profile
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
